import authService from './AuthService';
import databaseService from './DatabaseService';
import { t } from '../i18n';
import defaultNoteCover from '../assets/note-covers/default-note-cover.svg';

const BACKEND_ORIGIN = (process.env.REACT_APP_API_URL || 'http://localhost:3001/api').replace(/\/api\/?$/, '');
const coverImage = note => !note.coverUrl ? defaultNoteCover
  : /^(?:https?:\/\/|blob:|data:)/i.test(note.coverUrl) ? note.coverUrl : `${BACKEND_ORIGIN}${note.coverUrl}`;

const noteId = note => String(note?.noteId ?? note?.id ?? '');
const hasCounts = note => Number.isInteger(note?.flashcardCount) && note.flashcardCount > 0
  && Number.isInteger(note?.completedCardCount) && note.completedCardCount >= 0;

// Use whole-note server counters, not a filtered practice batch or a mutable card pool.
export async function noteCompletionSnapshot() {
  const userId = authService.getCurrentUserId();
  if (!userId) return null;
  try {
    const data = await databaseService.getMainPageData(userId);
    return Array.isArray(data?.notes) ? data.notes.map(note => ({
      id: noteId(note),
      title: note.title,
      coverUrl: note.coverUrl || null,
      flashcardCount: note.flashcardCount,
      completedCardCount: note.completedCardCount,
    })) : null;
  } catch (error) {
    console.warn('Failed to load note completion snapshot:', error);
    return null;
  }
}

export function getNewlyCompletedNotes(before, after, practicedCards = []) {
  if (!Array.isArray(before) || !Array.isArray(after) || !Array.isArray(practicedCards)) return [];
  const previous = new Map(before.map(note => [noteId(note), note]));
  const practiced = new Set(practicedCards.map(card => String(card?.noteId ?? '')).filter(Boolean));
  const seen = new Set();
  return after.filter(note => {
    const id = noteId(note);
    const old = previous.get(id);
    if (!id || seen.has(id) || !practiced.has(id) || !hasCounts(old) || !hasCounts(note)) return false;
    const completed = old.completedCardCount < old.flashcardCount
      && note.completedCardCount >= note.flashcardCount
      && note.completedCardCount > old.completedCardCount;
    if (completed) seen.add(id);
    return completed;
  });
}

export function noteCompletionCelebrations(before, after, practicedCards) {
  return getNewlyCompletedNotes(before, after, practicedCards).map(note => ({
    id: `note-complete:${noteId(note)}`,
    kind: 'noteComplete',
    colorTheme: 'level-up',
    title: t('noteCompletion.title'),
    message: t('noteCompletion.message', undefined, { noteTitle: note.title || t('noteCompletion.untitled') }),
    iconSrc: coverImage(note),
    iconAlt: t('dashboard.notes.cover', undefined, { title: note.title || t('noteCompletion.untitled') }),
    animationFrameSize: 240,
  }));
}
